// File bytes are committed before acknowledging the native share extension.
// Each account/season has its own draft; batch IDs make resume delivery idempotent.
export type StoredShareFile = {
  id: string;
  name: string;
  type: string;
  lastModified: number;
  blob: Blob;
};
export type SharedDraft = {
  key: string;
  text: string;
  files: StoredShareFile[];
  batches: string[];
};
const storeName = "shared-drafts";
export function sharedDraftKey(user: string, season: string) {
  return `${user}:${season}`;
}
function db(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("lift-and-learn-media-drafts", 1);
    request.onupgradeneeded = () =>
      request.result.createObjectStore(storeName, { keyPath: "key" });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error || new Error("Draft storage is unavailable"));
  });
}
export async function readSharedDraft(key: string): Promise<SharedDraft> {
  const database = await db();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, "readonly");
    const request = transaction.objectStore(storeName).get(key);
    request.onsuccess = () =>
      resolve(request.result || { key, text: "", files: [], batches: [] });
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => database.close();
  });
}
export function mergeSharedDraft(
  prior: SharedDraft,
  batch: { batchId: string; text: string; files: File[] },
): SharedDraft {
  if (prior.batches.includes(batch.batchId)) return prior;
  return {
    ...prior,
    text: [prior.text, batch.text].filter(Boolean).join("\n"),
    files: [
      ...prior.files,
      ...batch.files.map((file, index) => ({
        id: `${batch.batchId}:${index}`,
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
        blob: file,
      })),
    ],
    batches: [...prior.batches, batch.batchId],
  };
}
export async function persistSharedDraft(
  key: string,
  batch: { batchId: string; text: string; files: File[] },
): Promise<SharedDraft> {
  const database = await db();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    const request = store.get(key);
    let next: SharedDraft;
    request.onsuccess = () => {
      next = mergeSharedDraft(
        request.result || { key, text: "", files: [], batches: [] },
        batch,
      );
      store.put(next);
    };
    transaction.oncomplete = () => {
      database.close();
      resolve(next);
    };
    transaction.onerror = () => {
      database.close();
      reject(transaction.error || new Error("Could not save shared media"));
    };
    transaction.onabort = transaction.onerror;
  });
}
export async function consumeSharedDraft(
  key: string,
  files: string[],
  clearText = false,
) {
  const database = await db();
  return new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    const request = store.get(key);
    request.onsuccess = () => {
      const prior = request.result as SharedDraft | undefined;
      if (prior)
        store.put({
          ...prior,
          text: clearText ? "" : prior.text,
          files: prior.files.filter((file) => !files.includes(file.id)),
        });
    };
    transaction.oncomplete = () => {
      database.close();
      resolve();
    };
    transaction.onerror = () => {
      database.close();
      reject(transaction.error);
    };
  });
}
export function restoreSharedFile(file: StoredShareFile) {
  return new File([file.blob], file.name, {
    type: file.type,
    lastModified: file.lastModified,
  });
}
